import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import {
  convertLabelMapToConfig,
  measuredUsingConfigFor,
  mergeConfig,
  wrapAsDerivedFromMeasurementsConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import { GrainSize } from 'Components/Worksheets/MoistureContentWorksheet/types';
import QestIds from 'Components/Worksheets/QestIds';
import { ISieveSize } from 'Data/ViewModels/Equipment/ISieveSize';
import { FormatTypes } from 'Lib/Format';

/**
 * Test type ids where grain size should be shown on the wet mass entry grid
 */
export const GrainSizeTestTypeIds = [
  QestIds.moistureContent.AS_1289_2_1_1.rev2005,
  QestIds.moistureContent.BS_1377_2.rev1990,
  QestIds.moistureContent.BS_EN_ISO_17892_1.rev2014,
];

/**
 * Test type ids where Nominal Diameter should be shown on the wet mass entry grid
 */
export const NominalDiameterTestTypeIds = [QestIds.moistureContent.BS_EN_1097_5.rev2008];

const getConfig = (prefix: string) =>
  mergeConfig(
    {
      [`${prefix}.MoistureContentTest.VisualNominalParticleSize`]: ({ worksheet }: IConfigFunctionArgs) => ({
        visible:
          NominalDiameterTestTypeIds.includes(`${worksheet.type.Id}`) && worksheet?.data?.DryingTray?.Index === 0,
      }),
      [`${prefix}.MoistureContentTest.GrainSize`]: ({ worksheet }: IConfigFunctionArgs) => ({
        visible: GrainSizeTestTypeIds.includes(`${worksheet.type.Id}`),
      }),
      [`${prefix}.DryingTray.ContainerLabel`]: { maxLength: 20 },
      [`${prefix}.DryingTray.DateTimePlacedInOvenLocal`]: {
        format: { type: FormatTypes.Time, pattern: 'YYYY-MM-DD HH:mm' },
      },
    },
    getLabelConfig(prefix),
    getMeasuredUsingConfig(prefix),
    getDerivedFromMeasurementsConfig(prefix),
    {
      [`${prefix}.MoistureContentTest.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
    },
    getDropDownOptionsConfig(prefix)
  );

const getLabelConfig = (prefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.MoistureContentTest.VisualNominalParticleSize`]: 'Nominal Diameter (mm)',
      [`${prefix}.MoistureContentTest.GrainSize`]: 'Grain Size',
      [`${prefix}.MoistureContentTest.TotalWetMass`]: 'Wet Mass (g)',

      [`${prefix}.MoistureContentTest.Balance`]: 'Balance',
      [`${prefix}.MoistureContentTest.Oven`]: 'Oven',

      [`${prefix}.DryingTray.ContainerLabel`]: 'Tare ID',
      [`${prefix}.DryingTray.ContainerMass`]: 'Tare Mass',
      [`${prefix}.DryingTray.WetSoilAndContainerMass`]: 'Tare & Wet Mass',
      [`${prefix}.DryingTray.WetSoilMass`]: 'Wet Mass',
      [`${prefix}.DryingTray.DateTimePlacedInOvenLocal`]: 'Date and Time in Oven',
    })
  );

const getMeasuredUsingConfig = (prefix: string) => {
  const balance = measuredUsingConfigFor(`${prefix}.MoistureContentTest.Balance`);
  return {
    [`${prefix}.DryingTray.ContainerMass`]: balance,
    [`${prefix}.DryingTray.WetSoilAndContainerMass`]: balance,
  };
};

const getDerivedFromMeasurementsConfig = (prefix: string) => ({
  [`${prefix}.DryingTray.WetSoilMass`]: wrapAsDerivedFromMeasurementsConfig([
    `${prefix}.DryingTray.ContainerMass`,
    `${prefix}.DryingTray.WetSoilAndContainerMass`,
  ]),
});

const getDropDownOptionsConfig = (prefix: string) => ({
  [`${prefix}.MoistureContentTest.VisualNominalParticleSize`]: ({ worksheet }: IConfigFunctionArgs) => ({
    options: (worksheet.data?.MoistureContentTest?.AllVisualNominalParticleSizeSieves ?? []).map((s: ISieveSize) => ({
      value: s.Id,
      label: s.Caption,
    })),
  }),
  [`${prefix}.MoistureContentTest.GrainSize`]: ({ worksheet }: IConfigFunctionArgs) => {
    switch (worksheet.type.Id) {
      case QestIds.moistureContent.BS_EN_ISO_17892_1.rev2014:
        return {
          options: [
            { label: 'Fine', value: GrainSize.Fine },
            { label: 'Coarse', value: GrainSize.Coarse },
          ],
        };
      case QestIds.moistureContent.AS_1289_2_1_1.rev2005:
      case QestIds.moistureContent.BS_1377_2.rev1990:
        return {
          options: [
            { label: 'Fine', value: GrainSize.Fine },
            { label: 'Medium', value: GrainSize.Medium },
            { label: 'Coarse', value: GrainSize.Coarse },
          ],
        };
      default:
        return {};
    }
  },
});

export default getConfig('data');
